/*
  BossDrop – MVP (Create React App)
  =================================
  FILE: src/App.jsx
*/
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./index.css";
import logo from "./images/otherlogowhite.png";
import MobileDrawer from "./components/MobileDrawer";
import BackToTop from "./components/BackToTop";
import { AvatarColorProvider } from "./components/Avatar";

import { getOpenAIMessage } from "./chatgpt/aiClient";
import { safeParseJSON, normalizeAI } from "./chatgpt/helpers";

import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import Toaster from "./components/Toaster";
import ConfirmDialog from "./components/ConfirmDialog";
import { suggestHuddleSlotFromDecisions } from "./lib/scheduler";
import { compileWeeklyReport } from "./lib/report";
import { getDefaultAsana, coerceAsana } from "./helpers/data_utils";

import { proposeSlots } from "./helpers/calendar_utils";
import { detectRisksWithAI } from "./lib/report";
import { sanitizeTeamMembers } from "./lib/teamDirectory";
import { calendarWriteBackendShape } from "./lib/toolsInUse";
import { pilotShape } from "./lib/pilot";
import { meetingCalendarShape } from "./lib/meetingCalendar";
import { saveWorkspaceSettingsPatch } from "./lib/workspaceSettings";
// The one rule that decides whether this hub may rewrite the workspace's access
// map. Pure, unit-tested, and fails closed on anything it cannot read.
import {
  readDirectorySyncFailure,
  shouldMirrorTeamDirectory,
} from "./lib/workspaceDoc";

import ReportPage from "./pages/Report.js";
import SchedulerPage from "./pages/Scheduler.js";
import SettingsPage from "./pages/ControlRoom.js";
import SignInPage from "./pages/SignIn";
import AuditLogs from "./pages/AuditLogs";
import RequireAuth from "./components/RequireAuth";
import {
  APPROVALS_VIEW_PATH,
  PendingApprovalsBanner,
  ResetApprovalsNotice,
  usePendingApprovals,
} from "./components/PendingApprovals";
import KPIs from "./pages/KPIs.js";
import MemoryPage from "./pages/Memory.js";
import { SHOW_MEMORY_PAGE } from "./lib/navFlags.js";
import AccountPage from "./pages/Account.js";
import ClientAccountsPage from "./pages/ClientAccounts.js";
import { assembleReportText } from "./lib/text";
import { kpiConfigure, kpiLogReportDuration } from "./lib/kpi";
import { loadWorkspaceDocument } from "./api/awsClient"; // authenticated workspace document load (doc + docPresent)
import { Auth } from "./helpers/auth";
import {
  DEFAULT_EMAIL_RETENTION_DAYS,
  normalizeEmailRetentionDays,
} from "./lib/emailRetention";
import {
  otherTheme,
  readThemeChoice,
  saveThemeChoice,
  shownTheme,
} from "./lib/themeChoice";
const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.bossdrop.ai";

/* ----------------------- LocalStorage keys ----------------------- */
export const LS_KEYS = {
  asanaRaw: "bd:asanaRaw",
  projectScope: "bd:projectScope",
  asanaConnected: "bd:asanaConnected",
  jiraConnected: "bd:jiraConnected",
  automationMode: "bd:automationMode",
  slackConnected: "bd:slackConnected",
  slackPrefs: "bd:slackPrefs", // { teamId, teamName, includeChannels[], excludeChannels[], days }
  asanaPrefs: "bd:asanaPrefs",
  jiraPrefs: "bd:jiraPrefs",
  teamsConnected: "bd:teamsConnected",
  teamsPrefs: "bd:teamsPrefs",
  teamDir: "bd:teamDir",
  theme: "bd:theme",
  overdueDaysThreshold: "bd:overdueDaysThreshold",
  riskAmountThreshold: "bd:riskAmountThreshold",
  reportRecipients: "bd:reportRecipients", // string of comma-separated emails
  reportCron: "bd:reportCron", // e.g. "FRI 09:00 America/Los_Angeles"
  autoPingsEnabled: "bd:autoPingsEnabled", // "true"/"false"
  autoMeetingsEnabled: "bd:autoMeetingsEnabled", // "true"/"false"
  autoTasksEnabled: "bd:autoTasksEnabled", // "true"/"false"
  autoEscalationsEnabled: "bd:autoEscalationsEnabled", // "true"/"false"
  taskWriteBackend: "bd:taskWriteBackend",
  chatWriteBackend: "bd:chatWriteBackend",
  calendarWriteBackend: "bd:calendarWriteBackend",
  pilot: "bd:pilot",
  meetingCalendarEmail: "bd:meetingCalendarEmail",
  pollMinutes: "bd:pollMinutes",
  automationToolPrefs: "bd:automationToolPrefs",
  brainPrefs: "bd:brainPrefs",
  aiContextFiles: "bd:aiContextFiles",
};
const SESSION_EMAIL_KEY = "bd:sessionEmail";
const WORKSPACE_EMAIL_KEY = "bd:workspaceEmail";
const SIDEBAR_OPEN_KEY_PREFIX = "bd:sidebarOpen:";
const EXTRA_SESSION_KEYS = ["bd:selectedConnectors"];

function normalizeEmail(email) {
  return String(email || "")
    .trim()
    .toLowerCase();
}

function getSidebarOpenKey(email) {
  return `${SIDEBAR_OPEN_KEY_PREFIX}${normalizeEmail(email) || "guest"}`;
}

function lsGet(k) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function lsSet(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch {}
}
function ssGet(k) {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
}
function ssSet(k, v) {
  try {
    sessionStorage.setItem(k, v);
  } catch {}
}
function clearAppLocalState() {
  try {
    for (const k of Object.values(LS_KEYS)) localStorage.removeItem(k);
    localStorage.removeItem(SESSION_EMAIL_KEY);
    localStorage.removeItem(WORKSPACE_EMAIL_KEY);
    for (const k of [SESSION_EMAIL_KEY, WORKSPACE_EMAIL_KEY, ...EXTRA_SESSION_KEYS]) {
      sessionStorage.removeItem(k);
    }
  } catch {}
}

function clearLegacySessionArtifacts() {
  try {
    localStorage.removeItem(SESSION_EMAIL_KEY);
    localStorage.removeItem(WORKSPACE_EMAIL_KEY);
    localStorage.removeItem("bd:selectedConnectors");
  } catch {}
}

function seedLsFromSettings(s) {
  lsSet(LS_KEYS.theme, s.theme);
  lsSet(LS_KEYS.projectScope, s.projectScope || "");
  lsSet(LS_KEYS.brainPrefs, JSON.stringify(s.brainPrefs || {}));
  lsSet(LS_KEYS.aiContextFiles, JSON.stringify(s.aiContextFiles || []));
}

function normalizeAiContextFiles(value) {
  return (Array.isArray(value) ? value : [])
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      return {
        id: String(entry.id || "").trim(),
        sourceRef: String(entry.sourceRef || "").trim(),
        name: String(entry.name || entry.fileName || "").trim(),
        mimeType: String(entry.mimeType || "").trim(),
        extension: String(entry.extension || "").trim(),
        kind: String(entry.kind || "").trim(),
        sizeBytes: Number.isFinite(Number(entry.sizeBytes))
          ? Number(entry.sizeBytes)
          : null,
        uploadedAt: String(entry.uploadedAt || "").trim(),
        summary: String(entry.summary || "").trim(),
        keywords: Array.isArray(entry.keywords) ? entry.keywords : [],
        stakeholders: Array.isArray(entry.stakeholders) ? entry.stakeholders : [],
        deadlines: Array.isArray(entry.deadlines) ? entry.deadlines : [],
        deliverables: Array.isArray(entry.deliverables) ? entry.deliverables : [],
        risks: Array.isArray(entry.risks) ? entry.risks : [],
        extractedText: String(entry.extractedText || "").trim(),
      };
    })
    .filter((entry) => entry?.id && entry?.name);
}
function dedupeSlack(arr) {
  const seen = new Set();
  const out = [];
  for (const m of arr || []) {
    const key = `${m.channel || ""}-${m.ts || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(m);
  }
  return out;
}
// Accepts both S3 shapes. Exported for tests.
export function normalizeRemoteDoc(doc) {
  const s = (doc && (doc.settings || doc)) || {};
  const rawBrainPrefs =
    typeof s.brainPrefs === "object" && s.brainPrefs ? s.brainPrefs : {};
  const { recentFingerprints: _legacyRecentFingerprints, ...brainPrefs } =
    rawBrainPrefs;
  return {
    automationMode: s.automationMode ?? "manual",
    teamMembers: sanitizeTeamMembers(
      Array.isArray(s.teamMembers) ? s.teamMembers : [],
    ),
    overdueDaysThreshold: Number.isFinite(s.overdueDaysThreshold)
      ? s.overdueDaysThreshold
      : 3,
    riskAmountThreshold: Number.isFinite(s.riskAmountThreshold)
      ? s.riskAmountThreshold
      : 0,
    theme: s.theme ?? "dark",
    asanaConnected: !!s.asanaConnected,
    jiraConnected: !!s.jiraConnected,
    hubspotConnected: !!s.hubspotConnected,
    pipedriveConnected: !!s.pipedriveConnected,
    linearConnected: !!s.linearConnected,
    clickupConnected: !!s.clickupConnected,
    notionConnected: !!s.notionConnected,
    mondayConnected: !!s.mondayConnected,
    githubConnected: !!s.githubConnected,
    slackConnected: !!s.slackConnected,
    teamsConnected: !!s.teamsConnected,
    projectScope: typeof s.projectScope === "string" ? s.projectScope : "",
    teamsPrefs:
      typeof s.teamsPrefs === "object" && s.teamsPrefs ? s.teamsPrefs : {},

    asanaPrefs:
      typeof s.asanaPrefs === "object" && s.asanaPrefs ? s.asanaPrefs : null, // <—
    jiraPrefs:
      typeof s.jiraPrefs === "object" && s.jiraPrefs ? s.jiraPrefs : null,
    hubspotPrefs:
      typeof s.hubspotPrefs === "object" && s.hubspotPrefs ? s.hubspotPrefs : null,
    pipedrivePrefs:
      typeof s.pipedrivePrefs === "object" && s.pipedrivePrefs ? s.pipedrivePrefs : null,
    // Filters only (team ids, repo names). The server allowlists both on save,
    // so no org id, installation id or token ever lives in the doc.
    linearPrefs:
      typeof s.linearPrefs === "object" && s.linearPrefs ? s.linearPrefs : null,
    // Same idea for ClickUp (workspace + list ids), Notion (data source ids +
    // column maps) and monday.com (board ids, group, column maps). The server
    // allowlists each on save; tokens and provider account ids never live here.
    clickupPrefs:
      typeof s.clickupPrefs === "object" && s.clickupPrefs ? s.clickupPrefs : null,
    notionPrefs:
      typeof s.notionPrefs === "object" && s.notionPrefs ? s.notionPrefs : null,
    mondayPrefs:
      typeof s.mondayPrefs === "object" && s.mondayPrefs ? s.mondayPrefs : null,
    githubPrefs:
      typeof s.githubPrefs === "object" && s.githubPrefs ? s.githubPrefs : null,
    slackPrefs:
      typeof s.slackPrefs === "object" && s.slackPrefs ? s.slackPrefs : {},
    taskWriteBackend: s.taskWriteBackend ?? null,
    chatWriteBackend: s.chatWriteBackend ?? null,
    // The calendar meetings are booked on: "google" | "microsoft", or null for
    // the default (Google first, then Microsoft). Picked on the Control Room's
    // Auto-create meetings card.
    calendarWriteBackend: calendarWriteBackendShape(s.calendarWriteBackend),
    // Pilot mode (lib/pilot.js): { startDate, lengthDays, baselineDays, kpis,
    // endedAt }, or null when the workspace has never run a pilot.
    pilot: pilotShape(s.pilot),
    // Whose calendar BossDrop's own meetings are booked from: an address, or
    // null for the workspace owner (lib/meetingCalendar.js).
    meetingCalendarEmail: meetingCalendarShape(s.meetingCalendarEmail),
    pollMinutes: Number.isFinite(Number(s.pollMinutes))
      ? Number(s.pollMinutes)
      : 5, // ✅ NEW default
    automationToolPrefs:
      typeof s.automationToolPrefs === "object" && s.automationToolPrefs
        ? s.automationToolPrefs
        : {},
    // Settings for the shared ai@bossdrop.ai inbox. This function returns a
    // FIXED shape and ControlRoom's snapshot() rebuilds the whole settings
    // object from it, so a key missing here is dropped on load and then wiped
    // on the next save.
    aiInbox:
      typeof s.aiInbox === "object" && s.aiInbox && !Array.isArray(s.aiInbox)
        ? s.aiInbox
        : {},
    // Meeting notetaker (off by default). The server-stamped
    // consentAcceptedAt/By ride along (the server ignores them on save), but
    // the stored consentAccepted flag is dropped: only the Control Room's
    // explicit "Turn on" click may send it, so a stale tab saving later can't
    // re-accept consent after another admin turned the feature off.
    meetingNotetaker: stripStoredConsent(s.meetingNotetaker),
    // When BossDrop may book a meeting it picks the time for
    // (components/MeetingHoursCard.js). Kept as stored; the card and the
    // server (sanitizeMeetingHours) both read an absent or invalid value as
    // "each person's working day".
    meetingHours:
      typeof s.meetingHours === "object" &&
      s.meetingHours &&
      !Array.isArray(s.meetingHours)
        ? s.meetingHours
        : {},
    brainPrefs,
    aiContextFiles: normalizeAiContextFiles(s.aiContextFiles),
    emailTrackingDomains: Array.isArray(s.emailTrackingDomains)
      ? s.emailTrackingDomains.filter(
          (v) => typeof v === "string" && v.includes("@"),
        )
      : [],
    // Days BossDrop keeps tracked-email excerpts (lib/emailRetention.js).
    emailRetentionDays: normalizeEmailRetentionDays(s.emailRetentionDays),
  };
}

function stripStoredConsent(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const { consentAccepted: _storedConsent, ...rest } = value;
  return rest;
}

function projectTeamMembersForAi(teamMembers = []) {
  return (Array.isArray(teamMembers) ? teamMembers : [])
    .map((member) => ({
      id: String(member?.id || ""),
      name: String(member?.name || "").trim(),
      email: normalizeEmail(member?.email),
    }))
    .filter((member) => member.name || member.email)
    .sort((a, b) =>
      `${a.email || a.name || a.id}`.localeCompare(`${b.email || b.name || b.id}`),
    );
}

function useIsMobile(bp = 900) {
  const query = `(max-width:${bp}px)`;
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.matchMedia(query).matches : false,
  );
  React.useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    m.addEventListener
      ? m.addEventListener("change", onChange)
      : m.addListener(onChange);
    setIsMobile(m.matches);
    return () =>
      m.removeEventListener
        ? m.removeEventListener("change", onChange)
        : m.removeListener(onChange);
  }, [query]);
  return isMobile;
}

/* ----------------------------- App ------------------------------- */
export default function App() {
  return (
    <BrowserRouter>
      <InnerApp />
      <Toaster />
      <ConfirmDialog />
    </BrowserRouter>
  );
}

function InnerApp() {
  const BRAND = {
    base: "#665A9C",
    dark: "#4C4380",
    light: "#EFE9FA",
    accent: "#B7A4E6",
  };
  const isMobile = useIsMobile(900);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(
    () => lsGet(getSidebarOpenKey(ssGet(SESSION_EMAIL_KEY))) !== "false",
  );
  useEffect(() => {
    if (!isMobile && menuOpen) setMenuOpen(false);
  }, [isMobile, menuOpen]);
  const [aiRisks, setAiRisks] = useState([]);
  const [aiError, setAiError] = useState(null);
  // ---------- Boot gate ----------
  const [bootReady, setBootReady] = useState(false);
  const sessionEmailRef = useRef(normalizeEmail(ssGet(SESSION_EMAIL_KEY)));
  const workspaceEmailRef = useRef(normalizeEmail(ssGet(WORKSPACE_EMAIL_KEY)));
  const [currentUser, setCurrentUser] = useState(null);
  // A page that changes the signed-in person's own profile (Account) patches
  // the app's copy here, so every screen shows the change at once instead of
  // after a reload. Merged only into the SAME account, and skipped when
  // nothing changed so a background refresh does not re-render the app.
  const updateCurrentUser = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setCurrentUser((current) => {
      if (!current) return current;
      const patchEmail = normalizeEmail(patch.email);
      if (patchEmail && patchEmail !== normalizeEmail(current.email)) return current;
      const next = { ...current, ...patch };
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, []);
  useEffect(() => {
    clearLegacySessionArtifacts();
  }, []);
  useEffect(() => {
    const nextKey = getSidebarOpenKey(currentUser?.email || ssGet(SESSION_EMAIL_KEY));
    setDesktopSidebarOpen(lsGet(nextKey) !== "false");
  }, [currentUser?.email]);
  useEffect(() => {
    const nextKey = getSidebarOpenKey(currentUser?.email || ssGet(SESSION_EMAIL_KEY));
    lsSet(nextKey, desktopSidebarOpen ? "true" : "false");
  }, [currentUser?.email, desktopSidebarOpen]);
  const [workspaceOwnerEmail, setWorkspaceOwnerEmail] = useState(
    normalizeEmail(ssGet(WORKSPACE_EMAIL_KEY)),
  );
  // NO WORKSPACE UNTIL THE SERVER SAYS SO. These used to start at "owner" with
  // every permission true, which is the client-side half of the defect the
  // server just lost: an account whose workspace lookup came back empty was
  // shown an owner's hub. The server now reports securityLevel null and every
  // permission false for a session with no workspace record, and these defaults
  // agree with it, so nothing is unlocked before /auth/me has answered.
  const [workspaceSecurityLevel, setWorkspaceSecurityLevel] = useState("");
  const [workspacePermissions, setWorkspacePermissions] = useState({
    canManageWorkspace: false,
    canEditOwnConnections: false,
    canViewWorkspace: false,
    canInviteTeammates: false,
    canEditDirectory: false,
    canDeleteWorkspace: false,
  });
  // The workspace DOCUMENT's own state, which is a different question from who
  // you are:
  //   "loading"      /auth/me and the document read have not answered yet.
  //   "ready"        the document was read. An empty one is a real answer.
  //   "unavailable"  the read FAILED. Every setting on screen is a default,
  //                  not this workspace's, so nothing may be written back from
  //                  here -- saving would overwrite the workspace with blanks.
  //   "no-workspace" signed in, but this account holds no workspace record, so
  //                  there is no document to read at all.
  const [workspaceDocStatus, setWorkspaceDocStatus] = useState("loading");
  // The last refusal from the BOOTSTRAP mirror into the access map, or null.
  // `{ error, email, status }`. It survives across pages so the Team directory
  // panel can say the stored directory and the access map disagree, even when
  // the refusal happened on page load rather than on a save.
  const [workspaceDirectorySyncError, setWorkspaceDirectorySyncError] =
    useState(null);
  // The ONE copy of that state. The Control Room reports into it too (through
  // the outlet context) instead of keeping its own, because the Control Room
  // remounts on every navigation: a page-local copy brought a fixed bootstrap
  // refusal back after a round trip to another page, and dropped a real
  // refusal from a save. Every page report bumps this counter, so a slow
  // bootstrap mirror cannot land after (and overwrite) a newer answer.
  const directorySyncReportSeqRef = useRef(0);
  const reportWorkspaceDirectorySync = useCallback((next) => {
    directorySyncReportSeqRef.current += 1;
    setWorkspaceDirectorySyncError(next);
  }, []);
  // TRUE while a deliberate "remove everyone" (`clearDirectory: true`) has been
  // refused or failed and not yet re-sent successfully. A ref in App, not
  // Control Room state, so the retry survives the Control Room remounting
  // (which reseeds its idea of the access map from the now-empty document).
  // Lost on a full reload; the Team directory panel's "has access but not in
  // the directory" check is the backstop for that.
  const directoryClearPendingRef = useRef(false);

  // ---------- Settings state ----------
  const [automationMode, setAutomationMode] = useState("manual");
  const [teamMembers, setTeamMembers] = useState([]);
  const [theme, setTheme] = useState(lsGet(LS_KEYS.theme) || "dark");
  const [themeChoice, setThemeChoice] = useState(readThemeChoice);
  const currentTheme = shownTheme(themeChoice, theme);
  const [overdueDaysThreshold, setOverdueDaysThreshold] = useState(3);
  const [projectScope, setProjectScope] = useState("");
  const [riskAmountThreshold, setRiskAmountThreshold] = useState(100000);
  const [asanaConnected, setAsanaConnected] = useState(false);
  const [jiraConnected, setJiraConnected] = useState(false);
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [pipedriveConnected, setPipedriveConnected] = useState(false);
  const [linearConnected, setLinearConnected] = useState(false);
  const [clickupConnected, setClickupConnected] = useState(false);
  const [notionConnected, setNotionConnected] = useState(false);
  const [mondayConnected, setMondayConnected] = useState(false);
  const [githubConnected, setGithubConnected] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackPrefs, setSlackPrefs] = useState({});
  const [teamsConnected, setTeamsConnected] = useState(false);
  const [teamsPrefs, setTeamsPrefs] = useState({});
  const [asanaPrefs, setAsanaPrefs] = useState(null);
  const [jiraPrefs, setJiraPrefs] = useState(null);
  const [hubspotPrefs, setHubspotPrefs] = useState(null);
  const [pipedrivePrefs, setPipedrivePrefs] = useState(null);
  const [linearPrefs, setLinearPrefs] = useState(null);
  const [clickupPrefs, setClickupPrefs] = useState(null);
  const [notionPrefs, setNotionPrefs] = useState(null);
  const [mondayPrefs, setMondayPrefs] = useState(null);
  const [githubPrefs, setGithubPrefs] = useState(null);
  const [slackData, setSlackData] = useState([]); // normalized messages
  const [slackNextCursors, setSlackNextCursors] = useState({});
  const [taskWriteBackend, setTaskWriteBackend] = useState("planner");
  const [chatWriteBackend, setChatWriteBackend] = useState("teams");
  const [calendarWriteBackend, setCalendarWriteBackend] = useState(null);
  const [pilot, setPilot] = useState(null);
  const [meetingCalendarEmail, setMeetingCalendarEmail] = useState(null);
  const [teamsMessages, setTeamsMessages] = useState([]); // channel + chat msgs
  const [plannerTasks, setPlannerTasks] = useState([]); // Planner tasks
  const [pollMinutes, setPollMinutes] = useState(5);
  const [automationToolPrefs, setAutomationToolPrefs] = useState({});
  const [aiInbox, setAiInbox] = useState({});
  const [meetingNotetaker, setMeetingNotetaker] = useState({});
  const [meetingHours, setMeetingHours] = useState({});
  const [brainPrefs, setBrainPrefs] = useState({});
  const [aiContextFiles, setAiContextFiles] = useState([]);
  const [emailTrackingDomains, setEmailTrackingDomains] = useState([]);
  const [emailRetentionDays, setEmailRetentionDays] = useState(
    DEFAULT_EMAIL_RETENTION_DAYS,
  );
  const aiRelevantTeamMembersKey = useMemo(
    () => JSON.stringify(projectTeamMembersForAi(teamMembers)),
    [teamMembers],
  );
  const aiRelevantTeamMembers = useMemo(
    () => JSON.parse(aiRelevantTeamMembersKey || "[]"),
    [aiRelevantTeamMembersKey],
  );
  const pullTeams = useCallback(
    async ({ teamId, channels = [], chats = [], days } = {}) => {
      const tId = teamId || teamsPrefs?.teamId;
      const tenantId = teamsPrefs?.tenantId;

      if (!tenantId || !tId) return [];

      const includeChannels = channels.length
        ? channels
        : teamsPrefs?.includeChannels || [];

      const excludeChannels = teamsPrefs?.excludeChannels || [];

      const includeChats = chats.length
        ? chats
        : teamsPrefs?.includeChats || [];

      const lookbackDays = Number.isFinite(days)
        ? days
        : teamsPrefs?.days || 14;

      const post = async (url, body) => {
        const r = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok)
          throw new Error(j?.error || j?.message || "teams_request_failed");
        return j;
      };

      const [chanOut, chatOut] = await Promise.all([
        post(`${API}/api/teams/messages`, {
          tenant: tenantId,
          team: tId,
          includeChannels,
          excludeChannels,
          days: lookbackDays,
          cursors: {}, // keep empty for now
        }).catch(() => ({ messages: [] })),

        includeChats.length
          ? post(`${API}/api/teams/chat-messages`, {
              tenant: tenantId,
              chats: includeChats,
              days: lookbackDays,
              cursors: {},
            }).catch(() => ({ messages: [] }))
          : Promise.resolve({ messages: [] }),
      ]);

      const channelMessages = (chanOut?.messages || []).map((m) => ({
        ...m,
        _source: "channel",
      }));
      const chatMessages = (chatOut?.messages || []).map((m) => ({
        ...m,
        _source: "chat",
      }));

      const all = [...channelMessages, ...chatMessages];

      all.sort((a, b) => {
        const ta = new Date(a.ts || a.date || a.createdDateTime || 0).getTime();
        const tb = new Date(b.ts || b.date || b.createdDateTime || 0).getTime();
        return tb - ta;
      });

      return all;
    },
    [
      teamsPrefs?.tenantId,
      teamsPrefs?.teamId,
      teamsPrefs?.includeChannels,
      teamsPrefs?.excludeChannels,
      teamsPrefs?.includeChats,
      teamsPrefs?.days,
    ],
  );

  // Apply theme to DOM (no LS writes here)
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", currentTheme);
  }, [currentTheme]);

  // If auth cookie/user changes in another tab, force a clean rehydrate.
  useEffect(() => {
    if (!bootReady) return;
    let dead = false;
    let checking = false;

    const verifySession = async () => {
      if (dead || checking) return;
      checking = true;
      try {
        const me = await Auth.me().catch(() => null);
        const liveEmail = normalizeEmail(me?.user?.email);
        const expectedEmail = normalizeEmail(
          sessionEmailRef.current || ssGet(SESSION_EMAIL_KEY),
        );

        if (!expectedEmail) {
          if (liveEmail) {
            sessionEmailRef.current = liveEmail;
            ssSet(SESSION_EMAIL_KEY, liveEmail);
          }
          return;
        }

        if (!liveEmail || liveEmail !== expectedEmail) {
          clearAppLocalState();
          if (liveEmail) ssSet(SESSION_EMAIL_KEY, liveEmail);
          sessionEmailRef.current = liveEmail;
          window.location.reload();
        } else if (me?.user) {
          // Same account: pick up profile changes made in another tab.
          updateCurrentUser(me.user);
        }
      } finally {
        checking = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") verifySession();
    };

    window.addEventListener("focus", verifySession);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      dead = true;
      window.removeEventListener("focus", verifySession);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [bootReady, updateCurrentUser]);

  const pullPlanner = useCallback(
    async ({ teamId } = {}) => {
      const tId = teamId || teamsPrefs?.teamId;
      if (!tId) return [];

      const post = async (url, body) => {
        const r = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body || {}),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok)
          throw new Error(j?.error || j?.message || "Planner request failed");
        return j;
      };

      // NOTE: adjust body keys to match your backend if needed.
      // Most Teams teamId == M365 groupId, so using teamId here is usually correct.
      const plansRes = await post(`${API}/api/teams/planner/plans`, {
        teamId: tId,
      });

      const plans = plansRes?.plans || plansRes?.value || [];
      const planIds = plans.map((p) => p.id).filter(Boolean);

      if (!planIds.length) return [];

      // You MUST have a backend endpoint that returns tasks by planId.
      // If your backend path differs, change it here.
      const taskLists = await Promise.all(
        planIds.map((planId) =>
          post(`${API}/api/teams/planner/tasks`, { planId }).catch(() => ({
            tasks: [],
          })),
        ),
      );

      const tasks = taskLists.flatMap((x) => x?.tasks || x?.value || []);
      return tasks;
    },
    [teamsPrefs?.teamId],
  );
  const autoPulledTeamsRef = useRef(false);
  useEffect(() => {
    if (!bootReady) return;
    if (!teamsConnected) return;
    if (!teamsPrefs?.teamId) return;

    // You can choose to still pull Planner tasks even if no chat/channel targets:
    const shouldPullPlanner = true;

    if (autoPulledTeamsRef.current) return;
    autoPulledTeamsRef.current = true;

    (async () => {
      try {
        const msgs = await pullTeams();
        setTeamsMessages(msgs || []);
      } catch {
        setTeamsMessages([]);
      }

      if (shouldPullPlanner) {
        try {
          const tasks = await pullPlanner();
          setPlannerTasks(tasks || []);
        } catch {
          setPlannerTasks([]);
        }
      }
    })();
  }, [
    bootReady,
    teamsConnected,
    teamsPrefs?.teamId,
    teamsPrefs?.includeChannels,
    teamsPrefs?.includeChats,
    pullTeams,
    pullPlanner,
  ]);

  // ---------- Uploads (LS-backed) ----------
  const lastRanForPlain = useRef("");
  const insightsReqId = useRef(0);
  const [aiExecLoading, setAiExecLoading] = useState(false);
  const [aiExecSummary, setAiExecSummary] = useState("");
  const [decisionsLoading, setDecisionsLoading] = useState(false);
  const [decisions, setDecisions] = useState({
    mission_critical: [],
    this_week: [],
    next_week: [],
  });

  const [asanaData, setAsanaData] = useState([]);
  const [jiraData, setJiraData] = useState([]);
  const [linearData, setLinearData] = useState([]);
  const [clickupData, setClickupData] = useState([]);
  const [notionData, setNotionData] = useState([]);
  const [mondayData, setMondayData] = useState([]);
  const unifiedTasks = useMemo(() => {
    const asana = Array.isArray(asanaData) ? asanaData : [];
    const jira = Array.isArray(jiraData) ? jiraData : [];
    // Already in the Jira task shape (normalizeLinearIssues on the server).
    const linear = Array.isArray(linearData) ? linearData : [];
    // ClickUp, Notion and monday.com come back in that same shape too.
    const clickup = Array.isArray(clickupData) ? clickupData : [];
    const notion = Array.isArray(notionData) ? notionData : [];
    const monday = Array.isArray(mondayData) ? mondayData : [];
    const plannerNorm = (plannerTasks || []).map((t) => ({
      // normalize Planner -> Asana-ish task object
      id: t.id,
      name: t.title || t.name || "(untitled)",
      owner:
        t.owner ||
        t.assigneeEmail ||
        t.assignedTo?.[0]?.userEmail ||
        t.assignments?.[0]?.userEmail ||
        "",
      created_at: t.createdDateTime || t.created_at || null,
      due_on: t.dueDateTime || t.dueDate || t.due_on || null,
      completed:
        t.percentComplete === 100 || !!t.completedDateTime || !!t.completed_at,
      completed_at: t.completedDateTime || t.completed_at || null,
    }));

    return [
      ...asana,
      ...jira,
      ...linear,
      ...clickup,
      ...notion,
      ...monday,
      ...plannerNorm,
    ];
  }, [
    asanaData,
    jiraData,
    linearData,
    clickupData,
    notionData,
    mondayData,
    plannerTasks,
  ]);
  const unifiedMessages = useMemo(() => {
    const teamsNorm = (teamsMessages || []).map((m) => ({
      // match the Slack-ish shape your AI/report code expects
      date: m.date || m.createdDateTime || m.ts || null,
      text: m.text || m.body?.content || m.message || "",
      user_name: m.user_name || m.from?.user?.displayName || "Unknown",
      user_email: m.user_email || m.from?.user?.email || "",
    }));
    return [...(slackData || []), ...teamsNorm];
  }, [slackData, teamsMessages]);
  // ---- Is there any Asana data to analyze? ----
  const hasAnyData = useMemo(() => {
    const hasTasks = (unifiedTasks || []).length > 0;
    const hasMsgs = (unifiedMessages || []).length > 0;
    return hasTasks || hasMsgs;
  }, [unifiedTasks, unifiedMessages]);

  // inside InnerApp(), add this effect (e.g., after the other useEffects)
  useEffect(() => {
    if (!bootReady) return; // wait until settings are hydrated
    if (!asanaConnected) return; // only if the user has connected Asana

    let cancelled = false;

    (async () => {
      try {
        // Check connection + read saved prefs
        const s = await fetch(`${API}/api/asana/connected`, {
          credentials: "include",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : { connected: false }));

        if (!s.connected) return;

        const ws = s?.prefs?.workspace_gid || null;
        const project = s?.prefs?.project_gid || null;

        // If the user hasn't picked a workspace yet, we skip for now.
        if (!ws) return;

        const url = new URL(`${API}/api/asana/tasks`);
        url.searchParams.set("workspace", ws);
        if (project) url.searchParams.set("project", project);

        const j = await fetch(url, { credentials: "include" }).then((r) =>
          r.json(),
        );
        if (!cancelled) setAsanaData(j.tasks || []);
      } catch (e) {
        console.warn("Auto Asana sync failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootReady, asanaConnected, setAsanaData]);

  useEffect(() => {
    if (!bootReady) return;
    if (!jiraConnected) return;

    let cancelled = false;

    (async () => {
      try {
        const s = await fetch(`${API}/api/jira/connected`, {
          credentials: "include",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : { connected: false }));

        if (!s.connected) return;

        const siteId = s?.prefs?.siteId || null;
        const siteUrl = s?.prefs?.siteUrl || null;
        const project =
          s?.prefs?.projectKey || s?.prefs?.projectId || null;

        if (!siteId || !project) return;

        const url = new URL(`${API}/api/jira/issues`);
        url.searchParams.set("site", siteId);
        url.searchParams.set("project", project);
        if (siteUrl) url.searchParams.set("siteUrl", siteUrl);

        const j = await fetch(url, { credentials: "include" }).then((r) =>
          r.json(),
        );
        if (!cancelled) setJiraData(j.tasks || []);
      } catch (e) {
        console.warn("Auto Jira sync failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootReady, jiraConnected, setJiraData]);

  // The server reads the team filter from the workspace OWNER's doc, so this
  // only needs to know that teams are picked, not which ones.
  const linearTeamCount = Array.isArray(linearPrefs?.teamIds)
    ? linearPrefs.teamIds.length
    : 0;
  useEffect(() => {
    if (!bootReady) return;
    if (!linearConnected || !linearTeamCount) return;

    let cancelled = false;

    (async () => {
      try {
        const j = await fetch(`${API}/api/linear/issues`, {
          credentials: "include",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : { tasks: [] }));
        if (!cancelled) setLinearData(j.tasks || []);
      } catch (e) {
        console.warn("Auto Linear sync failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bootReady, linearConnected, linearTeamCount, setLinearData]);

  // ClickUp, Notion and monday.com work the same way as Linear: the server
  // reads the workspace, lists, databases or boards from the workspace OWNER's
  // doc, so these only need to know that something is picked.
  //
  // Each one runs ONCE per boot and connection, not on every pick. A pick in
  // the card reaches these prefs right away but its doc save waits 350ms, so a
  // sync fired per pick read the PREVIOUS selection (a 400 on the first pick,
  // one step stale after that) and spent the owner's 10/min read budget the
  // card's own pickers need. A selection made later is synced by the card's
  // "Sync now", which reads the saved doc.
  //
  // The ref holds a token for the current connection: taken the first time the
  // tool is booted and connected, cleared on disconnect so a reconnect syncs
  // again. The pull's answer lands only while its token is current and no pick
  // has changed the selection since it went out. That token, not an effect
  // cleanup, is what drops a stale answer, because a cleanup would also drop
  // the boot pull on StrictMode's second effect run. A refusal (400, 429)
  // leaves the tasks already on screen alone instead of emptying them.
  const clickupBootSync = useRef(null);
  const clickupListCount =
    clickupPrefs?.teamId && Array.isArray(clickupPrefs?.listIds)
      ? clickupPrefs.listIds.length
      : 0;
  useEffect(() => {
    if (!clickupConnected) {
      clickupBootSync.current = null;
      return;
    }
    if (!bootReady) return;
    const held = clickupBootSync.current;
    if (held) {
      if (held.count !== clickupListCount) held.stale = true;
      return;
    }
    const token = { count: clickupListCount, stale: false };
    clickupBootSync.current = token;
    if (!clickupListCount) return;

    (async () => {
      try {
        const j = await fetch(`${API}/api/clickup/tasks`, {
          credentials: "include",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : null));
        if (j && clickupBootSync.current === token && !token.stale) {
          setClickupData(j.tasks || []);
        }
      } catch (e) {
        console.warn("Auto ClickUp sync failed:", e);
      }
    })();
  }, [bootReady, clickupConnected, clickupListCount, setClickupData]);

  const notionBootSync = useRef(null);
  const notionSourceCount = Array.isArray(notionPrefs?.dataSourceIds)
    ? notionPrefs.dataSourceIds.length
    : 0;
  useEffect(() => {
    if (!notionConnected) {
      notionBootSync.current = null;
      return;
    }
    if (!bootReady) return;
    const held = notionBootSync.current;
    if (held) {
      if (held.count !== notionSourceCount) held.stale = true;
      return;
    }
    const token = { count: notionSourceCount, stale: false };
    notionBootSync.current = token;
    if (!notionSourceCount) return;

    (async () => {
      try {
        const j = await fetch(`${API}/api/notion/tasks`, {
          credentials: "include",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : null));
        if (j && notionBootSync.current === token && !token.stale) {
          setNotionData(j.tasks || []);
        }
      } catch (e) {
        console.warn("Auto Notion sync failed:", e);
      }
    })();
  }, [bootReady, notionConnected, notionSourceCount, setNotionData]);

  const mondayBootSync = useRef(null);
  const mondayBoardCount = Array.isArray(mondayPrefs?.boardIds)
    ? mondayPrefs.boardIds.length
    : 0;
  useEffect(() => {
    if (!mondayConnected) {
      mondayBootSync.current = null;
      return;
    }
    if (!bootReady) return;
    const held = mondayBootSync.current;
    if (held) {
      if (held.count !== mondayBoardCount) held.stale = true;
      return;
    }
    const token = { count: mondayBoardCount, stale: false };
    mondayBootSync.current = token;
    if (!mondayBoardCount) return;

    (async () => {
      try {
        const j = await fetch(`${API}/api/monday/tasks`, {
          credentials: "include",
          cache: "no-store",
        }).then((r) => (r.ok ? r.json() : null));
        if (j && mondayBootSync.current === token && !token.stale) {
          setMondayData(j.tasks || []);
        }
      } catch (e) {
        console.warn("Auto monday.com sync failed:", e);
      }
    })();
  }, [bootReady, mondayConnected, mondayBoardCount, setMondayData]);

  const slackKey = useMemo(() => {
    const slice = (slackData || [])
      .slice(0, 200)
      .map((m) => [m.ts, m.user_email || m.user_name, m.text]);
    try {
      return JSON.stringify(slice);
    } catch {
      return String(slice.length);
    }
  }, [slackData]);

  useEffect(() => {
    // If we already have a teamId saved, verify connection
    const team = slackPrefs?.teamId;
    if (!team) return;
    fetch(`${API}/api/slack/connected?team=${encodeURIComponent(team)}`, {
      credentials: "include",
      cache: "no-store",
    })
      .then((r) => r.json())
      .then((j) => {
        const ok = !!j.connected;
        setSlackConnected(ok);
        if (
          ok &&
          j.team_name &&
          (!slackPrefs.teamName || slackPrefs.teamName !== j.team_name)
        ) {
          const next = { ...slackPrefs, teamName: j.team_name };
          setSlackPrefs(next);
        }
      })
      .catch(() => {});
  }, [bootReady]); // eslint-disable-line

  const pullSlack = useCallback(
    async ({ append = false } = {}) => {
      if (!slackPrefs?.teamId) return [];
      const body = {
        team: slackPrefs.teamId,
        includeChannels: slackPrefs.includeChannels || [],
        excludeChannels: slackPrefs.excludeChannels || [],
        days: slackPrefs.days || 14,
        cursors: append ? slackNextCursors : {},
      };

      const r = await fetch(`${API}/api/slack/messages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const j = await r.json();

      // Functional set ensures we don’t lose updates on quick successive pulls
      setSlackData((prev) => {
        const merged = append
          ? [...prev, ...(j.messages || [])]
          : j.messages || [];
        const next = dedupeSlack(merged);
        console.log(
          "slackData set ->",
          next.length,
          "msgs (append:",
          append,
          ")",
        );
        return next;
      });

      setSlackNextCursors(j.nextCursors || {});
      return j.messages || [];
    },
    [
      API,
      slackPrefs?.teamId,
      slackPrefs?.includeChannels,
      slackPrefs?.excludeChannels,
      slackPrefs?.days,
      slackNextCursors,
    ],
  );
  // auto-pull once after boot+login+slack connect (also if teamId changes)
  const autoPulledRef = useRef(false);
  useEffect(() => {
    if (!bootReady) return;
    if (!slackPrefs?.teamId) return;
    if (!slackConnected) return;
    if (autoPulledRef.current) return;
    autoPulledRef.current = true;
    pullSlack({ append: false }).catch(() => {});
  }, [bootReady, slackConnected, slackPrefs?.teamId, pullSlack]);

  // ---- Scheduler state & helpers ----
  const [durationMin, setDurationMin] = useState(15);
  const [meetingTitle, setMeetingTitle] = useState(
    "Decision Huddle – Checkout v2",
  );
  const [meetingDesc, setMeetingDesc] = useState(
    "Review risks and approve scope or add capacity. Auto-generated by BossDrop.",
  );

  const proposedSlots = useMemo(
    () => proposeSlots({ count: 3, durationMin }),
    [durationMin],
  );

  const [suggestedSlot, setSuggestedSlot] = useState(null);
  useEffect(() => {
    setSuggestedSlot(suggestHuddleSlotFromDecisions(decisions));
    const top = [
      ...decisions.mission_critical,
      ...decisions.this_week,
      ...decisions.next_week,
    ].slice(0, 4);
    if (top.length)
      setMeetingDesc(
        `Decide on:\n• ${top.join("\n• ")}\n\nAuto-generated by BossDrop.`,
      );
  }, [decisions]);

  // ---------- Bootstrap settings from server with account checks ----------
  function BootstrapGate({ onReady }) {
    const ran = useRef(false);
    useEffect(() => {
      if (ran.current) return;
      ran.current = true;

      (async () => {
        const me = await Auth.me().catch(() => null);
        const email = normalizeEmail(me?.user?.email);
        // NO `|| email` fallback. The server now answers
        // `workspaceOwnerEmail: null` for a session that holds no workspace
        // record, and reading that as "then this person owns a workspace of
        // their own" is the client-side half of the defect the server just
        // lost: it filed KPIs, settings and saves under an address that owns
        // nothing. A missing answer means NO workspace, and the hub says so.
        const workspaceEmail = normalizeEmail(me?.user?.workspaceOwnerEmail);
        if (workspaceEmail) kpiConfigure({ email: workspaceEmail });
        if (!email) {
          clearAppLocalState();
          sessionEmailRef.current = "";
          workspaceEmailRef.current = "";
          setCurrentUser(null);
          setWorkspaceOwnerEmail("");
          setWorkspaceDocStatus("no-workspace");
          onReady();
          return;
        }

        const priorEmail = normalizeEmail(ssGet(SESSION_EMAIL_KEY));
        const switchedAccounts = !!priorEmail && priorEmail !== email;
        if (switchedAccounts) clearAppLocalState();

        // WHO you are, kept separate from WHAT this workspace has stored.
        // Identity and permissions come from /auth/me and stay true when the
        // workspace document cannot be read, so applying them is never
        // conditional on that read -- and, the other way round, the stored
        // settings are never applied without one.
        const applyIdentity = () => {
          ssSet(SESSION_EMAIL_KEY, email);
          ssSet(WORKSPACE_EMAIL_KEY, workspaceEmail);
          sessionEmailRef.current = email;
          workspaceEmailRef.current = workspaceEmail;
          setCurrentUser(me?.user || null);
          setWorkspaceOwnerEmail(workspaceEmail);
          setWorkspaceSecurityLevel(me?.user?.securityLevel || "");
          // No `||` fallback that invents permissions: a response without them
          // means no workspace, not full access.
          //
          // ONE exception, and it keys on the SERVER'S VERSION rather than on
          // anything a level could claim. An older backend answers /auth/me
          // with exactly {canManageWorkspace, canEditOwnConnections,
          // canViewWorkspace} and no `isOwner` key at all. Against that server
          // the three names this hub now reads are all undefined -> false for
          // EVERYONE INCLUDING THE OWNER, who then sees their own Team
          // directory fully disabled under a banner that says only the
          // workspace owner may change it. Deploy the backend first (the
          // security direction only tightens, so backend-first is always safe);
          // this is the belt and braces for the minutes in between. It cannot
          // re-open the hole, because it applies only when the response carries
          // no `isOwner` key whatsoever -- a new server always sends one -- and
          // it grants the owner nothing an old server would not already have
          // let them do.
          const serverUser = me?.user && typeof me.user === "object" ? me.user : {};
          const serverPermissions =
            serverUser.permissions && typeof serverUser.permissions === "object"
              ? serverUser.permissions
              : {};
          const serverAnswersOwnership = Object.prototype.hasOwnProperty.call(
            serverUser,
            "isOwner",
          );
          if (serverAnswersOwnership) {
            setWorkspacePermissions(serverPermissions);
          } else {
            const ownsThisWorkspace =
              !!email && !!workspaceEmail && email === workspaceEmail;
            setWorkspacePermissions({
              ...serverPermissions,
              canEditDirectory:
                serverPermissions.canEditDirectory ?? ownsThisWorkspace,
              canDeleteWorkspace:
                serverPermissions.canDeleteWorkspace ?? ownsThisWorkspace,
              canInviteTeammates:
                serverPermissions.canInviteTeammates ??
                !!serverPermissions.canManageWorkspace,
            });
          }
        };

        const applySettings = (s) => {
          seedLsFromSettings(s);
          applyIdentity();

          setAutomationMode(s.automationMode);
          setTeamMembers(s.teamMembers);
          setTheme(s.theme);
          setOverdueDaysThreshold(s.overdueDaysThreshold);
          setRiskAmountThreshold(s.riskAmountThreshold);
          setAsanaConnected(s.asanaConnected);
          setJiraConnected(!!s.jiraConnected);
          setHubspotConnected(!!s.hubspotConnected);
          setPipedriveConnected(!!s.pipedriveConnected);
          setLinearConnected(!!s.linearConnected);
          setClickupConnected(!!s.clickupConnected);
          setNotionConnected(!!s.notionConnected);
          setMondayConnected(!!s.mondayConnected);
          setGithubConnected(!!s.githubConnected);
          setSlackConnected(s.slackConnected);
          setSlackPrefs(s.slackPrefs || {});
          setProjectScope(s.projectScope || "");
          setAsanaPrefs(s.asanaPrefs || null);
          setJiraPrefs(s.jiraPrefs || null);
          setHubspotPrefs(s.hubspotPrefs || null);
          setPipedrivePrefs(s.pipedrivePrefs || null);
          setLinearPrefs(s.linearPrefs || null);
          setClickupPrefs(s.clickupPrefs || null);
          setNotionPrefs(s.notionPrefs || null);
          setMondayPrefs(s.mondayPrefs || null);
          setGithubPrefs(s.githubPrefs || null);
          setTeamsPrefs(s.teamsPrefs || {});
          setTeamsConnected(s.teamsConnected);
          setTaskWriteBackend(s.taskWriteBackend || "planner");
          setChatWriteBackend(s.chatWriteBackend || "teams");
          setCalendarWriteBackend(s.calendarWriteBackend || null);
          setPilot(s.pilot || null);
          setMeetingCalendarEmail(s.meetingCalendarEmail || null);
          setPollMinutes(s.pollMinutes ?? 5);
          setAutomationToolPrefs(s.automationToolPrefs || {});
          setAiInbox(s.aiInbox || {});
          setMeetingNotetaker(s.meetingNotetaker || {});
          setMeetingHours(s.meetingHours || {});
          setBrainPrefs(s.brainPrefs || {});
          setAiContextFiles(s.aiContextFiles || []);
          setEmailTrackingDomains(s.emailTrackingDomains || []);
          setEmailRetentionDays(normalizeEmailRetentionDays(s.emailRetentionDays));
        };

        // A session with NO workspace never reads a document: there is no
        // workspace whose document it would be. This is a real state the
        // server now reports (a teammate whose row was removed, an account
        // created before anyone listed it in a directory), and the hub renders
        // an explicit screen for it instead of an owner's hub full of blanks.
        if (!workspaceEmail) {
          applyIdentity();
          setWorkspaceDocStatus("no-workspace");
          onReady();
          return;
        }

        let loaded = null;
        try {
          loaded = await loadWorkspaceDocument(workspaceEmail);
        } catch (e) {
          // THE READ FAILED, which is a different answer from "this workspace
          // is empty" and must not be treated as one. Nothing below may run:
          // normalizeRemoteDoc would hand every setting its default, the
          // Control Room would autosave those defaults over the stored
          // document, and the mirror below would post an empty directory --
          // which is exactly how one database blip used to delete a
          // workspace's whole access map. The hub says "couldn't load" and
          // touches nothing.
          console.warn("Workspace document load failed.", e);
          applyIdentity();
          setWorkspaceDocStatus("unavailable");
          // A DIFFERENT account than the one this tab last held: leaving the
          // previous account's settings on screen would be worse than blank.
          // Writing these back is already impossible -- every save path is
          // gated on the document having loaded.
          if (switchedAccounts) applySettings(normalizeRemoteDoc({}));
          onReady();
          return;
        }

        try {
          const remoteDoc = loaded?.doc || {};
          const s = normalizeRemoteDoc(remoteDoc);
          applySettings(s);
          setWorkspaceDocStatus("ready");
          // Bootstrap re-mirrors the Team directory into the server's access
          // map. It must NEVER send an empty list: an empty list is what a
          // failed or empty read looks like, and posting it used to delete the
          // whole workspace's memberships (the server now refuses it outright,
          // and refuses it from anyone who is not the owner). So it fires only
          // when the DOCUMENT ITSELF carried a team list -- shouldMirrorTeamDirectory
          // reads the stored key rather than the normalised value, so a
          // document with no `teamMembers` key is an unanswered question and
          // not an empty directory -- and only for the owner, who is the only
          // person the sync route accepts.
          const bootstrapMembers = Array.isArray(s.teamMembers) ? s.teamMembers : [];
          // Ownership is the server's answer, not a level label: an owner is
          // the account whose address IS the workspace owner email. A teammate
          // an owner labelled "owner" in the directory is not one, and the
          // sync route refuses them anyway.
          const isWorkspaceOwner =
            me?.user?.isOwner === true ||
            (!!me?.user?.email &&
              !!me?.user?.workspaceOwnerEmail &&
              String(me.user.email).toLowerCase() ===
                String(me.user.workspaceOwnerEmail).toLowerCase());
          if (
            shouldMirrorTeamDirectory({
              docPresent: loaded?.docPresent,
              doc: remoteDoc,
              members: bootstrapMembers,
              isOwner: isWorkspaceOwner,
              // So the owner's own card (and rows with no usable address) do
              // not count as teammates: a directory holding nothing else is
              // refused by the server on every load otherwise.
              ownerEmail: workspaceEmail,
            })
          ) {
            const seqAtMirror = directorySyncReportSeqRef.current;
            // Quiet on the page ITSELF -- this fires on every load and must
            // not shout -- but NOT discarded. A refusal here means the stored
            // directory and the access map disagree, and the Team directory
            // panel shows a "workspace access wasn't updated" chip naming the
            // address the server refused. `.catch(() => {})` made a permanently
            // frozen access map look identical to a healthy one.
            // A Control Room result reported while this was in flight is
            // newer, so it wins.
            Auth.syncWorkspaceMemberships({
              teamMembers: bootstrapMembers,
            })
              .then(() => {
                if (directorySyncReportSeqRef.current !== seqAtMirror) return;
                setWorkspaceDirectorySyncError(null);
              })
              .catch((syncError) => {
                if (directorySyncReportSeqRef.current !== seqAtMirror) return;
                setWorkspaceDirectorySyncError({
                  ...readDirectorySyncFailure(syncError),
                  scope: "access",
                });
              });
          }
        } catch (e) {
          // The document was read; something after it was not. The settings on
          // screen may be half applied, so this is still not a state to write
          // a workspace back from.
          console.warn("Settings bootstrap failed.", e);
          setWorkspaceDocStatus("unavailable");
        } finally {
          onReady();
        }
      })();
    }, [onReady]);

    return null;
  }
  useEffect(() => {
    if (!unifiedTasks.length && !unifiedMessages.length) {
      setAiRisks([]);
      setAiError(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const { risks, error } = await detectRisksWithAI({
        asanaData: unifiedTasks,
        slackData: unifiedMessages,
        teamMembers: aiRelevantTeamMembers,
      });
      if (cancelled) return;
      setAiRisks(risks);
      setAiError(error);
    })();
    return () => {
      cancelled = true;
    };
  }, [unifiedTasks, unifiedMessages, aiRelevantTeamMembers]);
  // ---------- Report / Insights ----------
  const report = useMemo(
    () =>
      compileWeeklyReport({
        // Treat “tasks are available” as tasksConnected for the report engine
        asanaConnected: unifiedTasks.length > 0,
        asanaData: unifiedTasks,
        slackData: unifiedMessages,
        teamMembers,
        aiRisks,
        aiError, // optional, for friendly message
        overdueDaysThreshold,
        riskAmountThreshold,
      }),
    [
      unifiedTasks,
      unifiedMessages,
      teamMembers,
      aiRisks,
      aiError,
      overdueDaysThreshold,
      riskAmountThreshold,
    ],
  );

  // ---------- Report / Insights ----------
  useEffect(() => {
    const runKey = `${report.plaintext}::${slackKey}`;
    if (lastRanForPlain.current === runKey && hasAnyData) return;
    lastRanForPlain.current = runKey;

    // ---- No data? Set helpful defaults and bail. ----
    if (!hasAnyData) {
      setAiExecSummary(
        "Connect tools in Settings to generate a live weekly report with KPIs, risks, and smart decisions.",
      );
      setDecisions({
        mission_critical: ["Connect tools in Settings"],
        this_week: [],
        next_week: [],
      });
      return; // don't call OpenAI
    }

    (async () => {
      const toArr = (x) =>
        !x ? [] : Array.isArray(x) ? x.map(String) : [String(x)];
      const uniq = (arr) =>
        Array.from(new Set(toArr(arr).map((s) => s.trim()))).filter(Boolean);

      try {
        setAiExecLoading(true);
        setDecisionsLoading(true);
        const myId = ++insightsReqId.current;

        const ownerCandidates = Array.from(
          new Set(
            [
              ...(unifiedTasks || []).map((t) => t.owner).filter(Boolean),
              ...(report?.risks || []).map((r) => r.owner).filter(Boolean),
            ].filter(Boolean),
          ),
        ).slice(0, 50);

        const slackLines = (slackData || [])
          .slice(0, 200) // cap tokens
          .map(
            (m) =>
              `• [${new Date(m.date).toLocaleString()}] ${m.user_name}${
                m.user_email ? ` <${m.user_email}>` : ""
              }: ${m.text}`,
          );
        const prompt =
          `If there is no substantive data, return exactly:\n` +
          `{"summary":"Connect tools in Settings to generate a live weekly report with KPIs, risks, and smart decisions.","decisions":{"mission_critical":["Connect tools in Settings"],"this_week":[],"next_week":[]}}``Otherwise, return STRICT JSON ONLY for weekly insights with this schema:\n` +
          `{"summary":"<80-120 word paragraph>","decisions":{"mission_critical":["..."],"this_week":["..."],"next_week":["..."]}}\n\n` +
          `Rules:\n` +
          `- summary: 2–3 sentences, include key KPIs with numbers and trajectory, mention top 1–2 risks.\n` +
          `- decisions: each item ≤14 words; MUST use a strong verb {Authorize, Approve, Assign, Escalate, Decide, Cap, Reduce}; include owner; include clear deadline ("by Fri EOD" or "within 48h"); include measurable target if relevant; no vague verbs.\n` +
          `- Prefer owners from this list when possible: ${JSON.stringify(
            ownerCandidates,
          )}\n` +
          `- Output MUST be valid JSON only.\n\n` +
          `\nSlack (last ${slackPrefs?.days || 14}d):\n` +
          (slackLines.length
            ? slackLines.join("\n")
            : "(no Slack messages ingested)") +
          `\n\n` +
          `Weekly Report (plaintext):\n${report.plaintext}`;

        const raw = await getOpenAIMessage(prompt);
        const text = normalizeAI(raw);
        const parsed = safeParseJSON(text);
        if (!parsed || typeof parsed !== "object")
          throw new Error("Bad JSON from AI");
        if (myId !== insightsReqId.current) return;

        const nextSummary = String(parsed.summary || "").trim();
        const nextDecisions = parsed.decisions || {};
        const mc = uniq(nextDecisions.mission_critical || []);
        const tw = uniq(nextDecisions.this_week || []);
        const nw = uniq(nextDecisions.next_week || []);
        setAiExecSummary(nextSummary);
        setDecisions({ mission_critical: mc, this_week: tw, next_week: nw });
      } catch (e) {
        console.error("loadAIInsights error:", e);
        setAiExecSummary("");
        setDecisions({ mission_critical: [], this_week: [], next_week: [] });
      } finally {
        setAiExecLoading(false);
        setDecisionsLoading(false);
      }
    })();
  }, [report.plaintext, unifiedTasks, hasAnyData, slackKey, slackPrefs?.days]);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const sourceReport = useMemo(
    () => assembleReportText(report, aiExecSummary, decisions),
    [report.plaintext, aiExecSummary, decisions],
  );
  useEffect(() => {
    if (workspaceOwnerEmail) kpiConfigure({ email: workspaceOwnerEmail });
  }, [workspaceOwnerEmail]);

  async function askAI(opts = {}) {
    try {
      const wantServerPreview = !!opts?.force;
      if (wantServerPreview || !hasAnyData) {
        const previewRes = await fetch(`${API}/api/exec-reports/preview`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ days: slackPrefs?.days || teamsPrefs?.days || 14 }),
        }).catch(() => null);

        if (previewRes?.ok) {
          const previewJson = await previewRes.json().catch(() => ({}));
          const previewText = String(previewJson?.summary || "").trim();
          if (previewText) {
            setAiText(previewText);
            return previewText;
          }
        }
      }

      // If no data, don't ask the model—show a helpful message
      if (!hasAnyData) {
        const msg =
          "Connect tools in Settings to generate an exec-ready brief.";
        setAiText(msg);
        return msg; // ✅ IMPORTANT: return a string so Send Now isn't empty
      }

      setAiLoading(true);
      setAiText("");
      const t0 = performance.now();

      const summaryPrompt = `Create an exec-ready brief from the weekly report below with EXACTLY these sections:

Executive Summary:
- One paragraph. Include key KPIs and trend. No bullet list.

Risks:
- 3–5 bullets. Each: issue name, owner, short impact.

Decisions:
- 3–6 bullets. Each: strong verb + owner + deadline (e.g., "by Fri EOD") + metric if relevant.

Return plain TEXT only. No JSON.`;

      const out = await getOpenAIMessage(
        `${summaryPrompt}\n\nReport:\n${sourceReport}`,
      );
      const text = normalizeAI(out);
      setAiText(text.trim());

      const ms = performance.now() - t0;
      kpiLogReportDuration(ms, { chars: (text || "").length }).catch(() => {});
      return text.trim(); // ✅ you already had this
    } catch (e) {
      const msg = "(AI request failed – check server/.env)";
      setAiText(msg);
      return msg; // ✅ IMPORTANT: return a string so Send Now isn't empty
    } finally {
      setAiLoading(false);
    }
  }

  // Waiting approvals, fetched only for someone who may approve them: the
  // approve/deny routes require canManageWorkspace, so nobody else is told
  // about a queue they cannot act on.
  const pendingApprovals = usePendingApprovals({
    enabled:
      bootReady &&
      !!currentUser &&
      !!workspaceOwnerEmail &&
      !!workspacePermissions?.canManageWorkspace,
    workspaceKey: workspaceOwnerEmail,
  });

  // Saves top-level settings from a page other than the Control Room (the
  // KPIs page saves settings.pilot): only the keys given, merged over the
  // stored settings by the server, never while the document failed to load.
  // The caller updates the matching App state (setPilot) itself.
  const saveSettingsPatch = useCallback(
    (patch) =>
      saveWorkspaceSettingsPatch(patch, {
        docReady: workspaceDocStatus === "ready",
      }),
    [workspaceDocStatus],
  );

  const outletContext = {
    currentUser,
    // Patch the signed-in person's own profile after a save (see above).
    updateCurrentUser,
    currentUserEmail: normalizeEmail(currentUser?.email),
    workspaceOwnerEmail,
    workspaceSecurityLevel,
    workspacePermissions,
    canManageWorkspace: !!workspacePermissions?.canManageWorkspace,
    // { count, approvals, complete, dismissed, dismiss, refresh }: the hub
    // notice, the nav count and the Audit Logs approvals view.
    pendingApprovals,
    refreshPendingApprovals: pendingApprovals.refresh,
    // OWNER ONLY, both of them, and named separately from canManageWorkspace so
    // that no screen can gate a directory edit or a workspace deletion on "is
    // an admin" by accident. The server answers 403 either way; this is what
    // stops the hub offering a control that cannot work.
    canEditDirectory: !!workspacePermissions?.canEditDirectory,
    canDeleteWorkspace: !!workspacePermissions?.canDeleteWorkspace,
    canInviteTeammates: !!workspacePermissions?.canInviteTeammates,
    // The last refusal from mirroring the Team directory into the access map
    // (bootstrap or Control Room save), or null. `{ error, email, status,
    // scope }`; scope "doc" means the document save itself was refused.
    workspaceDirectorySyncError,
    // Pages report into the state above through this, never a copy of their own.
    setWorkspaceDirectorySyncError: reportWorkspaceDirectorySync,
    directoryClearPendingRef,
    // "loading" | "ready" | "unavailable" | "no-workspace". Any page that
    // WRITES must check it: only "ready" means the stored document was
    // actually read, and a save made from any other state overwrites a real
    // workspace with this page's defaults.
    workspaceDocStatus,
    // --- Report page data ---
    report,
    aiExecSummary,
    aiExecLoading,
    decisions,
    decisionsLoading,
    aiLoading,
    aiText,
    generateAiRewrite: askAI, // ← expose Topbar rewrite
    apiBase: API, // ← expose API base
    // --- Scheduler page (if you use these there) ---
    proposedSlots,
    durationMin,
    setDurationMin,
    meetingTitle,
    setMeetingTitle,
    meetingDesc,
    setMeetingDesc,
    suggestedSlot,

    // --- Settings state (from LS; updated by Settings page) ---
    automationMode,
    setAutomationMode,
    teamMembers,
    setTeamMembers,
    theme,
    currentTheme,
    toggleTheme: () => {
      const next = otherTheme(currentTheme);
      saveThemeChoice(next);
      setThemeChoice(next);
    },
    overdueDaysThreshold,
    setOverdueDaysThreshold,
    riskAmountThreshold,
    setRiskAmountThreshold,
    asanaConnected,
    setAsanaConnected,
    jiraConnected,
    setJiraConnected,
    hubspotConnected,
    setHubspotConnected,
    pipedriveConnected,
    setPipedriveConnected,
    linearConnected,
    setLinearConnected,
    clickupConnected,
    setClickupConnected,
    notionConnected,
    setNotionConnected,
    mondayConnected,
    setMondayConnected,
    githubConnected,
    setGithubConnected,

    // --- Uploads ---
    asanaData,
    setAsanaData,
    jiraData,
    setJiraData,
    linearData,
    setLinearData,
    clickupData,
    setClickupData,
    notionData,
    setNotionData,
    mondayData,
    setMondayData,
    asanaPrefs,
    setAsanaPrefs,
    jiraPrefs,
    setJiraPrefs,
    hubspotPrefs,
    setHubspotPrefs,
    pipedrivePrefs,
    setPipedrivePrefs,
    linearPrefs,
    setLinearPrefs,
    clickupPrefs,
    setClickupPrefs,
    notionPrefs,
    setNotionPrefs,
    mondayPrefs,
    setMondayPrefs,
    githubPrefs,
    setGithubPrefs,
    slackConnected,
    setSlackConnected,
    slackPrefs,
    setSlackPrefs,
    slackData,
    setSlackData,
    pullSlack,
    teamsConnected,
    setTeamsConnected,
    teamsPrefs,
    setTeamsPrefs,
    pullTeams,
    taskWriteBackend,
    setTaskWriteBackend,
    chatWriteBackend,
    setChatWriteBackend,
    calendarWriteBackend,
    setCalendarWriteBackend,
    pilot,
    setPilot,
    saveSettingsPatch,
    meetingCalendarEmail,
    setMeetingCalendarEmail,

    // Miscellaneous
    pollMinutes,
    setPollMinutes,
    automationToolPrefs,
    setAutomationToolPrefs,
    aiInbox,
    setAiInbox,
    meetingNotetaker,
    setMeetingNotetaker,
    meetingHours,
    setMeetingHours,
    brainPrefs,
    setBrainPrefs,
    aiContextFiles,
    setAiContextFiles,
    projectScope,
    setProjectScope,
    emailTrackingDomains,
    setEmailTrackingDomains,
    emailRetentionDays,
    setEmailRetentionDays,
  };

  return (
    <Routes>
      {/* Public route */}
      <Route
        path="/signin"
        element={
          <>
            {/* A fresh sign-in shows the approvals notice again. */}
            <ResetApprovalsNotice />
            <SignInPage />
          </>
        }
      />

      {/* Protected area: wait for account-aware settings bootstrap */}
      <Route
        path="/"
        element={
          <RequireAuth>
            {!bootReady ? (
              <BootScreen>
                <BootstrapGate onReady={() => setBootReady(true)} />
              </BootScreen>
            ) : (
              <ProtectedLayout
                brand={{
                  base: "#665A9C",
                  dark: "#4C4380",
                  light: "#EFE9FA",
                  accent: "#B7A4E6",
                }}
                logoSrc={logo}
                isMobile={isMobile}
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                desktopSidebarOpen={desktopSidebarOpen}
                setDesktopSidebarOpen={setDesktopSidebarOpen}
                topbarTitle="The coordinator's job, handled by AI"
                isPlatformAdmin={!!currentUser?.isPlatformAdmin}
                onRewrite={askAI}
                rewriteDisabled={aiLoading || aiExecLoading || decisionsLoading}
                onCopy={() => navigator.clipboard.writeText(sourceReport)}
                workspaceDocStatus={workspaceDocStatus}
                outletContext={outletContext}
              />
            )}
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/control-room" replace />} />
        <Route path="report" element={<ReportPage />} />
        {/*         <Route path="/scheduler" element={<SchedulerPage />} />
         */}{" "}
        <Route path="control-room" element={<SettingsPage />} />
        <Route path="kpis" element={<KPIs />} />{" "}
        {/* Memory can be switched off (lib/navFlags.js): with no route, /memory
            falls through to the catch-all below and lands on Control Room. */}
        {SHOW_MEMORY_PAGE ? (
          <Route path="memory" element={<MemoryPage />} />
        ) : null}
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="account" element={<AccountPage />} />
        {/* BossDrop's own admin area: creating the OWNER account for a new
            client company. Deliberately its own page, so it never shares a
            screen with a client's workspace settings, and never with the
            other invite path (a teammate joining THIS workspace, in Team
            Directory). The endpoint behind it is already platform-admin only;
            this redirect is defence in depth, and the nav item is hidden for
            everyone else. While the bootstrap /auth/me is still in flight we
            know neither answer yet, so this renders nothing rather than
            flashing the page or bouncing someone who does have the right. */}
        <Route
          path="clients"
          element={
            !bootReady ? null : currentUser?.isPlatformAdmin ? (
              <ClientAccountsPage />
            ) : (
              <Navigate to="/control-room" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/control-room" replace />} />
      </Route>
    </Routes>
  );
}

/* ---------------------- Simple boot screen ----------------------- */
function BootScreen({ children }) {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <div>
        <p className="muted" style={{ textAlign: "center" }}>
          Loading your workspace settings…
        </p>
        {children}
      </div>
    </div>
  );
}

/* ------------- Signed in, but in no workspace at all -------------- */
/* A real state since the server stopped treating "no membership record" as
   "owns their own workspace": the account is authenticated, every workspace
   route answers 403, and there is nothing to show. Saying so beats a hub of
   empty panels whose every button fails, and the Topbar above it still has
   Sign out.

   THE COPY IS DELIBERATELY NEUTRAL. The people who actually land here are
   mostly ones whose access was REMOVED on purpose -- an offboarding -- and
   their password and session still work, which is correct. The earlier wording
   framed that as a mistake, coached the person to lobby the colleague who had
   just removed them, and pointed them at support@bossdrop.ai with a promise to
   "put it right", which is not a promise BossDrop can keep about somebody
   else's workspace. It also turned every offboarding at every customer into a
   support email. It now states the fact and sends them to whoever runs their
   workspace. */
function NoWorkspaceNotice({ email }) {
  return (
    <section className="panel">
      <h2 style={{ marginTop: 0 }}>This account isn&rsquo;t in a workspace</h2>
      <p className="muted">
        {email ? <strong>{email}</strong> : "This account"} is signed in, but it
        is not part of a BossDrop workspace, so there are no settings, no
        reports and no team to show.
      </p>
      <p className="muted">
        If you think that&rsquo;s wrong, contact whoever manages your
        workspace. Access follows their Team directory.
      </p>
      <button
        type="button"
        className="btn"
        onClick={() => window.location.reload()}
      >
        Check again
      </button>
    </section>
  );
}

/* --------------------------- Chrome ------------------------------- */
function ProtectedLayout({
  brand,
  logoSrc,
  isMobile,
  menuOpen,
  setMenuOpen,
  desktopSidebarOpen,
  setDesktopSidebarOpen,
  topbarTitle,
  isPlatformAdmin = false,
  onRewrite,
  rewriteDisabled,
  onCopy,
  workspaceDocStatus = "ready",
  outletContext,
}) {
  const effectiveMenuOpen = isMobile ? menuOpen : desktopSidebarOpen;
  const location = useLocation();
  const navigate = useNavigate();
  const pendingApprovals = outletContext?.pendingApprovals;
  const pendingApprovalsCount = pendingApprovals?.count || 0;
  // Not on the Audit Logs page itself: the queue is already on screen there.
  const showApprovalsNotice =
    pendingApprovalsCount > 0 &&
    !pendingApprovals?.dismissed &&
    !location.pathname.startsWith("/audit-logs") &&
    workspaceDocStatus !== "no-workspace";

  React.useEffect(() => {
    const contentEl = document.querySelector(".content");
    contentEl?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    if (isMobile) setMenuOpen(false);
  }, [isMobile, location.pathname, setMenuOpen]);

  return (
    <div
      className={`app${!isMobile && !desktopSidebarOpen ? " sidebar-collapsed" : ""}`}
      style={{
        ["--brand-base"]: brand.base,
        ["--brand-dark"]: brand.dark,
        ["--brand-light"]: brand.light,
        ["--brand-accent"]: brand.accent,
      }}
    >
      <Sidebar
        logoSrc={logoSrc}
        open={desktopSidebarOpen}
        isPlatformAdmin={isPlatformAdmin}
        pendingApprovalsCount={pendingApprovalsCount}
      />

      {/* overlay for mobile drawer */}
      {isMobile ? (
        <div
          className={`drawerOverlay ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <div className="main">
        <Topbar
          title={topbarTitle}
          onRewrite={onRewrite}
          rewriteDisabled={rewriteDisabled}
          onCopy={onCopy}
          theme={outletContext?.currentTheme}
          onToggleTheme={outletContext?.toggleTheme}
          isMenuOpen={effectiveMenuOpen}
          onToggleMenu={() => {
            if (isMobile) {
              setMenuOpen((s) => !s);
              return;
            }
            setDesktopSidebarOpen((s) => !s);
          }}
        />

        {isMobile && (
          <MobileDrawer
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            isPlatformAdmin={isPlatformAdmin}
            pendingApprovalsCount={pendingApprovalsCount}
          />
        )}

        <div className="content" key={location.pathname}>
          {showApprovalsNotice ? (
            <PendingApprovalsBanner
              count={pendingApprovalsCount}
              approvals={pendingApprovals?.approvals}
              teamMembers={outletContext?.teamMembers}
              onReview={() => {
                // Acting on it counts as seeing it: no repeat this session.
                pendingApprovals?.dismiss?.();
                navigate(APPROVALS_VIEW_PATH);
              }}
              onDismiss={() => pendingApprovals?.dismiss?.()}
            />
          ) : null}
          {/* The read failed. Said once, at the top of whatever page you are
              on, because it is true of the whole hub: what is on screen is
              defaults, not your workspace. Every save path is disabled while
              this shows, so nothing here can overwrite what could not be
              read. */}
          {workspaceDocStatus === "unavailable" ? (
            <div className="infoBanner" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Couldn't load this workspace
              </div>
              <div className="muted small">
                {/* The first sentence is true everywhere. The second is only
                    true on the Control Room, which is the one page that WRITES
                    the workspace document -- saying "editing is off" on KPIs,
                    Audit Logs or Memory told people a page was broken when it
                    was working normally. */}
                Your settings and Team directory couldn&rsquo;t be read just
                now.{" "}
                {location.pathname.startsWith("/control-room")
                  ? "What you see below is not your saved workspace, and editing is off until the read succeeds, so nothing on screen can overwrite it."
                  : "This page still works; anything that depends on your saved settings may be missing."}
              </div>
              <button
                type="button"
                className="btn"
                style={{ marginTop: 8 }}
                onClick={() => window.location.reload()}
              >
                Try again
              </button>
            </div>
          ) : null}
          {/* No workspace at all: every page below it would 403. Account is
              the exception -- it is about the ACCOUNT, not the workspace, and
              it is where someone in this state goes to check who they are
              signed in as. */}
          {workspaceDocStatus === "no-workspace" &&
          !location.pathname.startsWith("/account") ? (
            <NoWorkspaceNotice email={outletContext?.currentUserEmail} />
          ) : (
            // Every badge on every page takes its color from the Team
            // directory: chosen colors first, then no two teammates alike.
            <AvatarColorProvider teamMembers={outletContext?.teamMembers}>
              <Outlet context={outletContext} />
            </AvatarColorProvider>
          )}
        </div>
        <BackToTop routeKey={location.pathname} />
      </div>
    </div>
  );
}
